import React, { useEffect, useContext, useCallback, useState } from "react";
import { UI } from "@wwimmo/ui";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { GetServiceDetailsByServiceType_uccm_v_customerservices_tiles as customerservices } from "src/api/generated/GetServiceDetailsByServiceType";
import { ServicesInfoModal } from "src/screens/services/ServicesInfoModal";
import styles from "./ServicesTiles.module.css";
import { NetworkConfig } from "src/network/NetworkConfig";
import { ServicesActivateProgressModal } from "./ServicesActivateProgressModal";
import { ServicesDeactivateProgressModal } from "./ServicesDeactivateProgressModal";

interface ServicesTileProperties {
    customerServices: customerservices[];
    serviceType: number;
}

export enum ServiceType {
    FEATURE = 10,
    INTEGRATION = 20
}

const ServicesTilesBase = (props: ServicesTileProperties) => {
    const {
        servicesInfoModalStore,
        servicesActivationProgressModalStore,
        servicesDeactivationProgressModalStore,
        servicesStore
    } = useContext(RootStoreContext);

    const {
        setIsActivationProgressModalDisplayed,
        isServiceSuccessfullyActivated,
        isActivationProgressModalDisplayed
    } = servicesActivationProgressModalStore;

    const {
        setIsDeactivationProgressModalDisplayed,
        isDeactivationProgressModalDisplayed,
        isServiceSuccessfullyDeactivated
    } = servicesDeactivationProgressModalStore;

    const { setIsServiceInfoModalDisplayed } = servicesInfoModalStore;

    const {
        setRefetchServicesData,
        activatedServices,
        setActivatedServices,
        updateServiceStatus,
        setCustomerServices,
        restoreServiceState,
        baseServiceId,
        arePrerequisitesMet
    } = servicesStore;

    const { t } = useTranslation();

    const [servicesIndex, setServicesIndex] = useState<number>();
    const [pendingServiceToActivate, setPendingServiceToActivate] = useState<string | null>(null);
    const [pendingServiceToDeactivate, setPendingServiceToDeactivate] = useState<string | null>(null);
    const [previousState, setPreviousState] = useState<boolean | null>(null);

    const customerServices = props.customerServices.length >= 1 ? props.customerServices : undefined;

    useEffect(() => {
        if (customerServices) {
            const updated: Record<string, boolean> = {};
            customerServices.forEach((s) => (updated[s.serviceid] = !!s.activated));
            setActivatedServices(updated);

            setCustomerServices(customerServices);
        }
    }, [customerServices, setActivatedServices, setCustomerServices]);

    const onOpenServiceInfoModal = useCallback(
        (index: number) => () => {
            setIsServiceInfoModalDisplayed(true);
            setServicesIndex(index);
        },
        [setIsServiceInfoModalDisplayed]
    );

    const onActivateServiceSwitch = useCallback(
        (serviceid: string) => (e: any) => {
            const checked = e?.target?.checked ?? e;

            if (customerServices) {
                const index = customerServices.findIndex((service) => service.serviceid === serviceid);
                setServicesIndex(index);
            }

            setIsServiceInfoModalDisplayed(false);

            setPreviousState(activatedServices[serviceid] ?? false);

            if (checked === true) {
                setPendingServiceToActivate(serviceid);
                setIsActivationProgressModalDisplayed(true);
                setIsDeactivationProgressModalDisplayed(false);
            } else {
                setPendingServiceToDeactivate(serviceid);
                setIsDeactivationProgressModalDisplayed(true);
                setIsActivationProgressModalDisplayed(false);
            }

            setRefetchServicesData(true);
        },
        [
            setIsActivationProgressModalDisplayed,
            setIsServiceInfoModalDisplayed,
            customerServices,
            setIsDeactivationProgressModalDisplayed,
            setRefetchServicesData,
            activatedServices
        ]
    );

    useEffect(() => {
        if (isActivationProgressModalDisplayed && pendingServiceToActivate) {
            updateServiceStatus(pendingServiceToActivate, true);
        }
    }, [isActivationProgressModalDisplayed, pendingServiceToActivate, updateServiceStatus]);

    useEffect(() => {
        if (isDeactivationProgressModalDisplayed && pendingServiceToDeactivate) {
            updateServiceStatus(pendingServiceToDeactivate, false);
        }
    }, [isDeactivationProgressModalDisplayed, pendingServiceToDeactivate, updateServiceStatus]);

    useEffect(() => {
        if (!isActivationProgressModalDisplayed && pendingServiceToActivate) {
            if (!isServiceSuccessfullyActivated && previousState !== null) {
                restoreServiceState(pendingServiceToActivate, previousState);

                if (servicesStore.lastBaseCascade && baseServiceId) {
                    restoreServiceState(baseServiceId, false);
                }
            }

            servicesStore.lastBaseCascade = false;
            setPendingServiceToActivate(null);
            setPreviousState(null);
        }
    }, [
        isActivationProgressModalDisplayed,
        pendingServiceToActivate,
        isServiceSuccessfullyActivated,
        previousState,
        restoreServiceState,
        baseServiceId,
        servicesStore
    ]);

    useEffect(() => {
        if (!isDeactivationProgressModalDisplayed && pendingServiceToDeactivate) {
            if (!isServiceSuccessfullyDeactivated && previousState !== null) {
                restoreServiceState(pendingServiceToDeactivate, previousState);

                Object.entries(servicesStore.lastDependentCascades).forEach(([dependentId, wasActive]) => {
                    restoreServiceState(dependentId, wasActive);
                });
            }

            servicesStore.lastDependentCascades = {};
            setPendingServiceToDeactivate(null);
            setPreviousState(null);
        }
    }, [
        isDeactivationProgressModalDisplayed,
        pendingServiceToDeactivate,
        isServiceSuccessfullyDeactivated,
        previousState,
        restoreServiceState,
        servicesStore
    ]);

    return (
        <>
            {customerServices?.map((service, index) => {
                const sid = service.serviceid;
                const isActive = activatedServices[sid] ?? false;
                const isLocked = !isActive && !arePrerequisitesMet(service.feature);
                return (
                    <UI.Col sm={6} lg={4} xl={3} key={sid}>
                        <UI.Card className={styles.FeatureCard} square title={service.title ?? ""}>
                            {servicesStore.canSwitchService(service) ? (
                                <>
                                    <div
                                        className={`${styles.ActivateServiceSwitch} ${
                                            isLocked ? styles.SwitchLocked : ""
                                        }`}
                                    >
                                        <UI.Switch
                                            checked={isActive}
                                            onToggle={onActivateServiceSwitch(sid)}
                                            disabled={isLocked}
                                        />
                                    </div>
                                    {isLocked && (
                                        <div className={styles.RequiresPrereqHint}>
                                            {t("screens.services.features_and_integrations.requires_kredi_flow_pro")}
                                        </div>
                                    )}
                                </>
                            ) : undefined}

                            <UI.Row className={styles.FeatureRow}>
                                <UI.Col className={styles.Thumbnail}>
                                    {service.servicesfiles.length > 0 && (
                                        <img
                                            src={`${NetworkConfig.proxy}/files/services/${sid}/de/${service.servicesfiles[0].id}.${service.servicesfiles[0].extension}`}
                                            alt={service.servicesfiles[0].id}
                                        />
                                    )}
                                </UI.Col>
                                <UI.Col className={styles.TeaserText}>{service.teaser}</UI.Col>
                            </UI.Row>

                            <div className={styles.ModalLinkContainer}>
                                <div className={styles.ModalLink} onClick={onOpenServiceInfoModal(index)}>
                                    {`${t("screens.services.features_and_integrations.more")}...`}
                                </div>
                            </div>
                        </UI.Card>
                    </UI.Col>
                );
            })}

            <ServicesInfoModal
                customerService={customerServices?.[servicesIndex ?? 0]}
                serviceType={props.serviceType}
            />
            <ServicesDeactivateProgressModal customerService={customerServices?.[servicesIndex ?? 0]} />
            <ServicesActivateProgressModal customerService={customerServices?.[servicesIndex ?? 0]} />
        </>
    );
};

export const ServicesTiles = Sentry.withProfiler(observer(ServicesTilesBase));
